import { useEffect, useMemo, useState } from 'react'

export interface UseAccordionExpandResult {
  expandedItems: string[]
  allExpanded: boolean
  toggleAll: () => void
  onAccordionSelect: (eventKey: string | string[] | null | undefined) => void
}

export default function useAccordionExpand<T extends { id: number | string }>(records: T[]): UseAccordionExpandResult {
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const recordKeys = useMemo(() => records.map(record => String(record.id)), [records])

  useEffect(() => {
    const validKeys = new Set(recordKeys)
    setExpandedItems(prev => {
      const next = prev.filter(key => validKeys.has(key))
      return next.length === prev.length ? prev : next
    })
  }, [recordKeys])

  const allExpanded = recordKeys.length > 0 && recordKeys.every(key => expandedItems.includes(key))

  const toggleAll = () => {
    setExpandedItems(allExpanded ? [] : recordKeys)
  }

  const onAccordionSelect = (eventKey: string | string[] | null | undefined) => {
    if (Array.isArray(eventKey)) {
      setExpandedItems(eventKey)
      return
    }

    if (!eventKey) {
      setExpandedItems([])
      return
    }

    // In current alwaysOpen mode eventKey is typically an array, but keep this
    // toggle path for compatibility if accordion behavior/config changes and a
    // single key string is emitted (e.g., single-open mode).
    setExpandedItems(prev => prev.includes(eventKey)
                             ? prev.filter(key => key !== eventKey)
                             : [...prev, eventKey])
  }

  return { expandedItems, allExpanded, toggleAll, onAccordionSelect }
}
